import React, { useEffect, useRef, useState } from 'react';
import { useAuth, errorMessage } from '../../context/AuthContext';
import { PlanRow, usePlanActions, fmtSize, ACCEPT } from '../../components/PlanParts';

// Admin des plans : ajouter un plan (titre + autant de fichiers PDF/Word que l'on veut → bucket S3 saisi ici)
export default function AdminPlans() {
  const { authFetch } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loadError, setLoadError] = useState(false);
  const [form, setForm] = useState({ title: '', bucket: '', region: 'eu-west-3' });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);
  const fileInput = useRef(null);

  const load = () => authFetch('/api/plans')
    .then(r => r.ok ? r.json() : Promise.reject())
    .then(d => { setPlans(d); setLoadError(false); })
    .catch(() => setLoadError(true));
  const actions = usePlanActions(authFetch, load, setFlash);

  useEffect(() => {
    load();
    // bucket et région : valeurs utilisées la dernière fois (ou celles du .env)
    authFetch('/api/admin/plans/config').then(r => r.ok ? r.json() : null).then(c => { if (c) setForm(f => ({ ...f, bucket: c.bucket || '', region: c.region || f.region })); }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  // Les fichiers s'accumulent : on peut en choisir plusieurs d'un coup, puis en rajouter d'autres
  const pick = (e) => {
    const added = Array.from(e.target.files || []);
    e.target.value = '';
    setFiles(cur => [...cur, ...added.filter(a => !cur.some(c => c.name === a.name && c.size === a.size))]);
  };

  const add = async (e) => {
    e.preventDefault();
    if (files.length === 0) { setFlash({ type: 'error', text: 'Ajoutez au moins un fichier PDF ou Word.' }); return; }
    setBusy(true); setFlash(null);
    try {
      const fd = new FormData();
      fd.append('title', form.title);
      fd.append('bucket', form.bucket);
      fd.append('region', form.region);
      files.forEach(f => fd.append('files', f));
      const r = await authFetch('/api/admin/plans', { method: 'POST', body: fd });
      if (!r.ok) throw new Error(await errorMessage(r));
      setForm(f => ({ ...f, title: '' }));
      setFiles([]);
      setFlash({ type: 'ok', text: `✓ plan ajouté (${files.length} fichier${files.length > 1 ? 's' : ''})` });
      setTimeout(() => setFlash(null), 2500);
      load();
    } catch (err) { setFlash({ type: 'error', text: `Plan non ajouté : ${err.message}` }); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <h1 style={{ fontStyle: 'italic', marginBottom: '1.5rem' }}>Plans</h1>
      {flash && <div className={`flash ${flash.type === 'error' ? 'error' : ''}`}>{flash.text}</div>}

      <form onSubmit={add} className="qcm-panel" style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.3rem', marginBottom: '1rem' }}>+ Ajouter un plan</h2>
        <div className="form-group">
          <label htmlFor="plan-title">Titre du plan</label>
          <input id="plan-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="ex : Plan de révision — droit des contrats" required maxLength={200} />
        </div>
        <div className="qcm-grid-2">
          <div className="form-group">
            <label htmlFor="plan-bucket">Nom du bucket S3</label>
            <input id="plan-bucket" value={form.bucket} onChange={e => setForm({ ...form, bucket: e.target.value.trim() })} placeholder="ex : mon-bucket-plans" required autoCapitalize="none" autoCorrect="off" spellCheck="false" />
          </div>
          <div className="form-group">
            <label htmlFor="plan-region">Région du bucket</label>
            <input id="plan-region" value={form.region} onChange={e => setForm({ ...form, region: e.target.value.trim() })} placeholder="eu-west-3" required autoCapitalize="none" spellCheck="false" />
          </div>
        </div>
        <div className="form-group">
          <label>Fichiers (PDF, DOC ou DOCX — 20 Mo max chacun)</label>
          <input ref={fileInput} type="file" multiple accept={ACCEPT} onChange={pick} hidden id="plan-file" />
          <div>
            <button type="button" className="btn secondary" onClick={() => fileInput.current && fileInput.current.click()}>{files.length ? '+ ajouter d\'autres fichiers' : '+ choisir des fichiers'}</button>
          </div>
          {files.length > 0 && (
            <ul className="plan-pending">
              {files.map((f, i) => (
                <li key={`${f.name}-${f.size}-${i}`}>
                  <span className="plan-pending-name">{f.name}</span>
                  <span className="plan-file-size">{fmtSize(f.size)}</span>
                  <button type="button" className="plan-file-del" onClick={() => setFiles(files.filter((_, j) => j !== i))} aria-label={`Retirer ${f.name}`}>✕</button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <p className="plan-hint">Autant de fichiers que vous voulez (15 par envoi) : vous pourrez aussi en ajouter plus tard depuis la liste. Le bucket reste privé : les visiteurs passent par le lien du site. Le serveur doit avoir le droit d'écrire dans ce bucket (rôle IAM : s3:PutObject, s3:GetObject, s3:DeleteObject).</p>
        <button className="btn" type="submit" disabled={busy}>{busy ? 'envoi en cours…' : `ajouter le plan${files.length > 1 ? ` (${files.length} fichiers)` : ''}`}</button>
      </form>

      {loadError && <div className="flash error">Impossible de charger les plans — rechargez la page.</div>}
      {!loadError && plans.length === 0 && <div className="empty-state">aucun plan pour l'instant.</div>}
      <ul className="note-list">{plans.map(p => <PlanRow key={p.id} p={p} actions={actions} />)}</ul>
    </div>
  );
}
